<template>
  <div class="sap-status-matrix" role="table" aria-label="SAP portal status">
    <div class="sap-status-matrix__header" role="row">
      <div class="sap-status-matrix__corner" role="columnheader">
        <div class="sap-status-matrix__title">CASS Health</div>
        <div class="sap-status-matrix__countdown">{{ countdown }}</div>
      </div>
      <div
        v-for="environment in environments"
        :key="environment"
        class="sap-status-matrix__environment"
        role="columnheader"
      >
        <v-chip small :color="environmentColor(environment)" dark>
          {{ environment }}
        </v-chip>
      </div>
    </div>
    <div
      v-for="row in rows"
      :key="row.portal"
      class="sap-status-matrix__row"
      role="row"
    >
      <div class="sap-status-matrix__portal" role="rowheader">
        {{ row.label }}
      </div>
      <div
        v-for="environment in environments"
        :key="environment"
        class="sap-status-matrix__cell"
        role="cell"
      >
        <a
          :href="portalUrl(environment, row.portal)"
          target="_blank"
          rel="noopener noreferrer"
          :title="`Open ${row.label} for ${environment}`"
          class="sap-status-matrix__link"
        >
          <span :class="statusTextClass(portalStatus(environment, row.portal).status)">
            {{ portalStatus(environment, row.portal).status }}
          </span>
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import { getSAPPortalUrl } from "../utils/developerPortal";
import { SAPAPI_ENVIRONMENTS } from "../utils/sapapiDiagnostics";

const STATUS_ROWS = Object.freeze([
  { portal: "devportal", label: "SAP Developer Portal" },
  { portal: "apiportal", label: "SAP API Portal" },
]);

export default {
  name: "SAPPortalStatusMatrix",
  props: {
    countdown: {
      type: String,
      required: true,
    },
    portals: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      environments: SAPAPI_ENVIRONMENTS,
      rows: STATUS_ROWS,
    };
  },
  methods: {
    environmentColor(environment) {
      if (environment === "PRODUCTION") return "#006c27";
      if (environment === "QUALITY") return "#fccf46";
      return "#1d9bb2";
    },
    portalStatus(environment, portal) {
      return (
        this.portals.find(
          (item) => item.environment === environment && item.portal === portal
        ) || { status: "LOADING" }
      );
    },
    portalUrl(environment, portal) {
      return getSAPPortalUrl(environment, portal);
    },
    statusTextClass(status) {
      if (status === "ONLINE") return "sap-status-matrix__status sap-status-matrix__status--online";
      if (status === "WARNING") return "sap-status-matrix__status sap-status-matrix__status--warning";
      if (status === "OFFLINE") return "sap-status-matrix__status sap-status-matrix__status--offline";
      return "sap-status-matrix__status sap-status-matrix__status--loading";
    },
  },
};
</script>

<style scoped>
.sap-status-matrix {
  border: 1px solid #dfe5e8;
  border-radius: 4px;
  overflow: hidden;
}

.sap-status-matrix__header,
.sap-status-matrix__row {
  display: grid;
  grid-template-columns: minmax(145px, 1.25fr) repeat(3, minmax(0, 1fr));
}

.sap-status-matrix__header {
  background: #f5f7f8;
  border-bottom: 1px solid #dfe5e8;
}

.sap-status-matrix__corner {
  padding: 8px 12px;
  text-align: left;
}

.sap-status-matrix__title {
  color: #37474f;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.1;
}

.sap-status-matrix__countdown {
  color: #78909c;
  font-size: 0.68rem;
  line-height: 1.1;
}

.sap-status-matrix__environment {
  padding: 9px 8px;
  text-align: center;
}

.sap-status-matrix__row + .sap-status-matrix__row {
  border-top: 1px solid #edf0f2;
}

.sap-status-matrix__portal {
  align-items: center;
  color: #546e7a;
  display: flex;
  font-size: 0.78rem;
  font-weight: 600;
  padding: 10px 12px;
}

.sap-status-matrix__cell {
  align-items: center;
  border-left: 1px solid #edf0f2;
  display: flex;
}

.sap-status-matrix__link {
  align-items: center;
  display: flex;
  justify-content: center;
  min-height: 40px;
  text-decoration: none;
  width: 100%;
}

.sap-status-matrix__link:hover,
.sap-status-matrix__link:focus {
  background: #f5f7f8;
}

.sap-status-matrix__status {
  font-size: 0.9rem;
  font-weight: 700;
}

.sap-status-matrix__link:hover .sap-status-matrix__status,
.sap-status-matrix__link:focus .sap-status-matrix__status {
  text-decoration: underline;
}

.sap-status-matrix__status--online {
  color: #2e7d32;
}

.sap-status-matrix__status--warning {
  color: #ef6c00;
}

.sap-status-matrix__status--offline {
  color: #c62828;
}

.sap-status-matrix__status--loading {
  color: #78909c;
}

@media (max-width: 760px) {
  .sap-status-matrix__header,
  .sap-status-matrix__row {
    grid-template-columns: minmax(118px, 1.2fr) repeat(3, minmax(0, 1fr));
  }

  .sap-status-matrix__status {
    font-size: 0.72rem;
  }

  .sap-status-matrix__portal {
    font-size: 0.68rem;
    padding-left: 8px;
  }
}
</style>
